import { useEffect, useState, useRef, useCallback } from "react";
import { Typography } from "@mui/material";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";
import { useInfiniteHits } from "react-instantsearch";
import { useDebounce } from "react-use";

import { useUsers } from "hooks";
import { trackSearch } from "tracking/google/analytics";
import { getInMemoryCache } from "utils/algoliaUtils";

import MasonryGrid from "./MasonryGrid";
import StinkyBox from "./StinkyBox";

const cache = getInMemoryCache();

const InfiniteSearchResults = ({ searchTerm, modulePosition, isLoading, skeletonSlot }) => {
  const { currentUserLoaded } = useUsers();
  const previousUserLoadedState = useRef(currentUserLoaded);
  const transformItems = useCallback((items) => {
    return items?.map((item) => ({
      ...item,
      shouldTrackAlgoliaAnalytics: true,
    }));
  }, []);
  const { items, showMore, isLastPage } = useInfiniteHits({ transformItems, cache });
  const [debounceSearchTerm, setDebounceSearchTerm] = useState("");
  const sentinel = useRef(null);

  useEffect(() => {
    if (!previousUserLoadedState.current && currentUserLoaded) {
      cache.invalidate();
      previousUserLoadedState.current = currentUserLoaded;
    }
  }, [currentUserLoaded]);

  useEffect(() => {
    if (!sentinel.current) return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isLastPage && !isLoading) {
          showMore();
        }
      });
    });

    observer.observe(sentinel.current);

    return () => {
      observer.disconnect();
    };
  }, [isLastPage, showMore, isLoading]);

  useDebounce(() => setDebounceSearchTerm(searchTerm), 1000, [searchTerm]);

  useEffect(() => {
    if (!debounceSearchTerm) return;

    trackSearch(debounceSearchTerm);
  }, [debounceSearchTerm]);

  const renderGridContent = () => {
    return (
      <>
        <MasonryGrid hits={items} modulePosition={modulePosition} />
        {!isLastPage && (
          <Typography variant="h2" align="center" ref={sentinel}>
            Loading More...
          </Typography>
        )}
      </>
    );
  };

  const renderCards = () => <div>{renderGridContent()}</div>;
  const renderStinkyBox = () => <StinkyBox />;

  if (!searchTerm) return null;
  if (isLoading && isEmpty(items) && skeletonSlot) return skeletonSlot;

  return isEmpty(items) && !isLoading ? renderStinkyBox() : renderCards();
};

InfiniteSearchResults.propTypes = {
  modulePosition: PropTypes.number,
  searchTerm: PropTypes.string,
  isLoading: PropTypes.bool,
  skeletonSlot: PropTypes.node,
};

export default InfiniteSearchResults;
